import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Empty, Select, SelectProps, Spin } from 'antd';
import { debounce } from 'lodash';

import Service, { Variable, Variables } from '../../../lib/App';
import { Text } from '../../components';

interface IVariablesSelection {
  variableName: string | undefined;
  placeholder?: string;
  onChange: (value: any) => void;
}

interface VariablesPayload {
  isLoading: boolean;
  error: string;
  data: Variables;
}

const VariablesSelection: FC<IVariablesSelection & SelectProps> = ({
  variableName = undefined,
  placeholder = '',
  onChange,
}) => {
  const [searchVariable, setSearchVariable] = useState<string>(undefined);
  const [variablesPayload, setVariablesPayload] = useState<VariablesPayload>({
    isLoading: false,
    error: null,
    data: null,
  });
  const [variable, setVariable] = useState<Variable>(null);

  const getVariables = () => {
    setVariablesPayload({ isLoading: true, error: null, data: null });

    Service.planner.fetchVariables({}).then((data: Variables) => {
      setVariablesPayload({ isLoading: false, error: null, data });
    });
  };

  useEffect(() => {
    getVariables();
  }, []);

  useEffect(() => {
    setSearchVariable(variableName);
  }, [variableName]);

  const delayedVariablesPayloadUpdate = () => {
    setTimeout(() => {
      setVariablesPayload({ isLoading: false, error: null, data: null });
    }, 500);
  };

  useEffect(() => {
    if (!searchVariable) {
      setVariable(null);
      getVariables();
      return;
    }

    setVariablesPayload({ isLoading: true, error: null, data: null });

    Service.planner
      .fetchVariables({
        name: searchVariable,
      })
      .then(
        (data: Variables) => {
          setVariablesPayload({ isLoading: false, error: null, data });
        },
        () => {
          delayedVariablesPayloadUpdate();
        },
      );
  }, [searchVariable]);

  function renderOptions(): ReactNode[] {
    if (variable)
      return [
        <Select.Option key={variable?.name} value={variable.name}>
          {variable.name}
        </Select.Option>,
      ];
    else
      return variablesPayload?.data?.items?.map((variable: Variable) => (
        <Select.Option key={variable.name} value={variable.name}>
          {variable.name}
        </Select.Option>
      ));
  }

  return (
    <Select
      placeholder={placeholder || 'Select an option'}
      style={{
        width: '100%',
      }}
      allowClear
      showSearch
      value={searchVariable}
      notFoundContent={
        variablesPayload?.isLoading ? (
          <div>
            <Spin size="small" style={{ margin: '0 8px' }} />
            <Text>Searching...</Text>
          </div>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )
      }
      onSearch={debounce((e) => {
        setSearchVariable(e);

        onChange(e);
      }, 500)}
      onChange={(value) => {
        setSearchVariable(value);

        onChange(value);
      }}
    >
      {renderOptions()}
    </Select>
  );
};

export default VariablesSelection;
